import type { ListView } from '@app/constants/list-views';
import { globalSplitManager } from '@app/signal/splitLayout';
import { hapticImpact } from '@core/mobile/haptics';
import { AnimatedCallIcon } from '@icon/wide-call';
import { AnimatedChannelIcon } from '@icon/wide-channel';
import { AnimatedEmailIcon } from '@icon/wide-email';
import { AnimatedFileMdIcon } from '@icon/wide-fileMd';
import { AnimatedInboxIcon } from '@icon/wide-inbox';
import { AnimatedStarIcon } from '@icon/wide-star';
import { AnimatedTaskIcon } from '@icon/wide-task';
import { useLocation } from '@solidjs/router';
import { cn, Layer } from '@ui';
import { type Component, createSignal, type JSX, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { useSplitLayout } from '../split-layout/layout';

const ICON_ANIMATION_DURATION_MS = 500;

type MobileDockButtonProps = {
  icon: Component<
    JSX.SvgSVGAttributes<SVGSVGElement> | { triggerAnimation?: boolean }
  >;
  label?: string;
  onClick: () => void;
  active?: boolean;
  ref?: HTMLButtonElement | ((el: HTMLButtonElement) => void);
  onTouchMove?: (e: TouchEvent) => void;
  onTouchEnd?: (e: TouchEvent) => void;
  iconClass?: string;
};

function MobileDockButton(props: MobileDockButtonProps) {
  const [animating, setAnimating] = createSignal(false);

  return (
    <button
      type="button"
      ref={props.ref}
      onPointerDown={() => {
        hapticImpact('light');
        setAnimating(true);
        setTimeout(() => setAnimating(false), ICON_ANIMATION_DURATION_MS);
        props.onClick();
      }}
      onTouchMove={props.onTouchMove}
      onTouchEnd={props.onTouchEnd}
      class={cn(
        'flex flex-col items-center justify-center flex-1 pt-3 pb-2 bg-surface border-t border-edge-muted',
        props.active && 'text-accent'
      )}
    >
      <div class={cn('size-6 [&_svg]:size-6', props.iconClass)}>
        <Dynamic component={props.icon} triggerAnimation={animating()} />
      </div>
      <Show when={props.label}>
        <span class="text-xs">{props.label}</span>
      </Show>
    </button>
  );
}

export function MobileDock() {
  const { openWithSplit } = useSplitLayout();
  const location = useLocation();

  const isActive = (id: ListView) => {
    const activeContent = globalSplitManager()?.activeSplit()?.content();
    if (!activeContent) {
      const segments = location.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] === id;
    }
    return activeContent.id === id;
  };

  const navigate = (id: ListView) => {
    // If we're already on a soup/component view, replace in-place (mergeHistory)
    // so the tab switch doesn't push a new entry into the swipe-back BG slot.
    // From any other view (document, task, etc.) treat it as forward navigation
    // so the user can swipe back to where they were.
    const fgContent = globalSplitManager()?.activeSplit()?.content();
    const isOnSoupView = fgContent?.type === 'component';
    openWithSplit({ type: 'component', id }, { mergeHistory: isOnSoupView });
  };

  return (
    <Layer depth={1}>
      <div class="relative z-mobile-nav-bar flex flex-row justify-between">
        <div class="-z-1 absolute inset-x-0 top-0 w-screen h-40 bg-surface" />
        <MobileDockButton
          icon={AnimatedInboxIcon}
          active={isActive('inbox')}
          onClick={() => {
            navigate('inbox');
          }}
        />
        <MobileDockButton
          icon={AnimatedEmailIcon}
          active={isActive('mail')}
          onClick={() => navigate('mail')}
        />
        <MobileDockButton
          icon={AnimatedChannelIcon}
          active={isActive('channels')}
          onClick={() => {
            navigate('channels');
          }}
        />
        <MobileDockButton
          icon={AnimatedTaskIcon}
          active={isActive('tasks')}
          onClick={() => navigate('tasks')}
        />
        <MobileDockButton
          icon={AnimatedFileMdIcon}
          active={isActive('documents')}
          onClick={() => navigate('documents')}
        />
        <MobileDockButton
          icon={AnimatedStarIcon}
          active={isActive('agents')}
          onClick={() => navigate('agents')}
        />
        <MobileDockButton
          icon={AnimatedCallIcon}
          active={isActive('calls')}
          onClick={() => navigate('calls')}
        />
      </div>
    </Layer>
  );
}
